import moment from 'moment';

// Excel column mapping to API fields
export const EXCEL_COLUMN_MAPPING = {
  'S.NO.': 'serial',
  'POINT OF HIRE': 'point_of_hire',
  'EMPLOYEE ID': 'employee_id',
  'EMPLOYEE NAME': 'name',
  'NATIONALITY': 'nationality',
  'ACTUAL PROFESSION': 'actual_profession',
  'VISA PROFESSION': 'visa_profession',
  'CLIENT': 'client',
  'PROJECT': 'project',
  'VISA PROVIDER': 'visa_provider',
  'BORDER NUMBER': 'border_number',
  'IQAMA NUMBER': 'iqama_number',
  'PROJECT HEAD': 'project_head',
  'SLA EMP ID': 'sla_emp_id',
  'APPSHEET STATUS': 'appsheet_status',
  'ARRIVAL DATE': 'arrival_date',
  'AGING (in days)': 'aging',
  'STATUS': 'status',
  'CHANGE CLIENT, SALARY & OTHERS': 'change_client_salary',
  'SELECT': 'select',
  'basic': 'basic',
  'food': 'food',
  'hra': 'hra',
  'travel_allowance': 'travel_allowance',
  'fixed_ot': 'fixed_ot',
  'mobile_allowance': 'mobile_allowance',
  'air_ticket_allowance': 'air_ticket_allowance',
  'additional_ot': 'additional_ot',
  'other_allowance': 'other_allowance',
  'WORK LOCATION': 'work_location',
  'MOBILIZATION DATE': 'mobilization_date'
};

// Required columns for validation
export const REQUIRED_COLUMNS = [
  'EMPLOYEE ID',
  'MOBILIZATION DATE'
];

// Salary component columns
export const SALARY_COLUMNS = [
  'basic',
  'food',
  'hra',
  'travel_allowance',
  'fixed_ot',
  'mobile_allowance',
  'air_ticket_allowance',
  'additional_ot',
  'other_allowance'
];

/**
 * Validate Excel data — mirrors MobilizationFormPopUp submit validations:
 *   1. EMPLOYEE ID required
 *   2. MOBILIZATION DATE required
 *   3. MOBILIZATION DATE must not be before ARRIVAL DATE (if present)
 *   4. MOBILIZATION DATE must not be in the future
 * @param {Array} data - Parsed Excel data
 * @returns {Object} - { errors, warnings, validData }
 */
export const validateExcelData = (data) => {
  const errors = [];
  const warnings = [];
  const validData = [];

  if (!Array.isArray(data) || data.length === 0) {
    errors.push('Excel file is empty or contains no valid data rows');
    return { errors, warnings, validData };
  }

  // Check required columns exist
  const availableColumns = Object.keys(data[0]).filter(k => k !== '_rowIndex');
  const missingColumns = REQUIRED_COLUMNS.filter(col => !availableColumns.includes(col));
  if (missingColumns.length > 0) {
    errors.push(`Missing required columns: ${missingColumns.join(', ')}`);
    return { errors, warnings, validData };
  }

  const today = moment().startOf('day');

  data.forEach((row, index) => {
    const rowNumber = row._rowIndex || index + 2;
    const rowErrors = [];

    // 1. EMPLOYEE ID required
    const employeeId = String(row['EMPLOYEE ID'] || '').trim();
    if (!employeeId) {
      rowErrors.push('EMPLOYEE ID is required');
    }
    // 2. MOBILIZATION DATE required
    const mobRaw = row['MOBILIZATION DATE'];
    const mobStr = String(mobRaw || '').trim();
    if (!mobStr) {
      rowErrors.push('MOBILIZATION DATE is required');
    } else {
      const mobDate = parseExcelDate(mobRaw);
      if (!mobDate.isValid()) {
        rowErrors.push('MOBILIZATION DATE is invalid');
      } else {
        // 3. Must not be before ARRIVAL DATE
        const arrRaw = row['ARRIVAL DATE'];
        if (arrRaw && String(arrRaw).trim() !== '') {
          const arrDate = parseExcelDate(arrRaw);
          if (arrDate.isValid() && mobDate.isBefore(arrDate, 'day')) {
            rowErrors.push('Mobilization date must be >= arrival date');
          }
        }

        // 4. Must not be in the future
        if (mobDate.isAfter(today, 'day')) {
          rowErrors.push('Mobilization date should not be > current date');
        }
      }
    }
    

    if (rowErrors.length > 0) {
      const empId = String(row['EMPLOYEE ID'] || '').trim();
      const label = empId ? `Row ${rowNumber} [${empId}]` : `Row ${rowNumber}`;
      errors.push(`${label}: ${rowErrors.join(', ')}`);
    } else {
      validData.push(row);
    }
  });

  // Warn on duplicate employee IDs
  const seen = new Set();
  const dupes = new Set();
  validData.forEach(row => {
    const id = String(row['EMPLOYEE ID'] || '').trim();
    if (id) {
      if (seen.has(id)) dupes.add(id);
      else seen.add(id);
    }
  });
  if (dupes.size > 0) {
    warnings.push(`Duplicate employee IDs found: ${[...dupes].join(', ')}`);
  }

  return { errors, warnings, validData };
};

/**
 * Parse Excel date value to moment object
 * @param {*} dateValue - Excel date value
 * @returns {moment.Moment} - Parsed date
 */
export const parseExcelDate = (dateValue) => {
  if (!dateValue) return moment.invalid();

  // Already a JS Date object
  if (dateValue instanceof Date) {
    return moment(dateValue);
  }

  // Numeric serial OR string that is purely numeric — Excel serial date
  const numVal = typeof dateValue === 'number' ? dateValue : (String(dateValue).match(/^\d+$/) ? parseInt(dateValue, 10) : null);
  if (numVal !== null) {
    // Excel serial: days since 1900-01-01, with the leap-year-1900 bug (subtract 2 not 1)
    return moment('1899-12-30').add(numVal, 'days');
  }

  // String date — try known formats
  if (typeof dateValue === 'string') {
    const formats = [
      'YYYY-MM-DD',   // normalized from cellDates:true
      'D-MMM-YYYY',   // 15-Mar-2026
      'DD-MMM-YYYY',  // 05-Mar-2026
      'D-MMM-YY',     // 2-Jun-25
      'DD-MMM-YY',    // 02-Jun-25
      'DD/MM/YYYY',
      'MM/DD/YYYY',
      'DD-MM-YYYY',
    ];

    for (const format of formats) {
      const parsed = moment(dateValue, format, true);
      if (parsed.isValid()) return parsed;
    }
  }

  return moment(dateValue);
};

/**
 * Map Excel data to API format
 * @param {Array} excelData - Validated Excel data
 * @returns {Array} - Mapped data for API
 */
export const mapExcelToApiFormat = (excelData) => {
  return excelData.map(row => {
    // Calculate total salary from components
    let totalSalary = 0;
    const salaryComponents = {};
    
    SALARY_COLUMNS.forEach(col => {
      const value = row[col];
      let numValue = 0;
      
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        const cleanValue = String(value).replace(/[^\d.-]/g, '');
        numValue = parseFloat(cleanValue) || 0;
      }
      
      salaryComponents[col] = numValue;
      totalSalary += numValue;
    });
    
    // Parse dates
    const arrivalDate = parseExcelDate(row['ARRIVAL DATE']);
    const mobilizationDate = parseExcelDate(row['MOBILIZATION DATE']);
    // Helper function to safely get string value
    const safeString = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    };
    
    return {
      // Employee information
      employee_id: safeString(row['EMPLOYEE ID']),
      name: safeString(row['EMPLOYEE NAME']),
      nationality: safeString(row['NATIONALITY']),
      actual_profession: safeString(row['ACTUAL PROFESSION']),
      visa_profession: safeString(row['VISA PROFESSION']),
      
      // Project information
      project: safeString(row['PROJECT']),
      client: safeString(row['CLIENT']),
      visa_provider: safeString(row['VISA PROVIDER']),
      
      // Location and dates
      work_location: safeString(row['WORK LOCATION']),
      arrival_date: arrivalDate.isValid() ? arrivalDate.format('YYYY-MM-DD') : null,
      mobilization_date: mobilizationDate.isValid() ? mobilizationDate.format('YYYY-MM-DD') : null,
      employee_contract_start_Date: arrivalDate.isValid() ? arrivalDate.toISOString() : null,
      
      // Salary components
      ...salaryComponents,
      Basicsalary: salaryComponents.basic,
      Totalsalary: totalSalary,
      
      // Additional fields
      border_number: safeString(row['BORDER NUMBER']),
      iqama_number: safeString(row['IQAMA NUMBER']),
      point_of_hire: safeString(row['POINT OF HIRE']),
      status: safeString(row['STATUS']),
      
      // Metadata
      _originalRow: row._rowIndex,
      _calculatedTotalSalary: totalSalary
    };
  }).filter(employee => {
    // Final filter: employee_id (candidate_id) is the ONLY mandatory key
    // Everything else is optional or validated earlier
    return employee.employee_id && employee.employee_id.trim() !== '';
  });
};

/**
 * Group mapped data by project
 * @param {Array} mappedData - Mapped Excel data
 * @returns {Object} - Data grouped by project
 */
export const groupDataByProject = (mappedData) => {
  const grouped = {};
  
  mappedData.forEach(employee => {
    const projectName = employee.project;
    
    if (!grouped[projectName]) {
      grouped[projectName] = {
        projectName,
        client: employee.client,
        mobilization_date: employee.mobilization_date,
        work_location: employee.work_location,
        employees: [],
        totalEmployees: 0,
        totalSalary: 0,
        // These will be resolved later via API calls
        project_id: null,
        client_id: null,
        isValidProject: false,
        validationErrors: []
      };
    }
    
    grouped[projectName].employees.push(employee);
    grouped[projectName].totalEmployees += 1;
    grouped[projectName].totalSalary += employee.Totalsalary;
  });
  
  return grouped;
};

/**
 * Validate project data against database
 * @param {Object} projectGroup - Project group data
 * @param {Array} availableProjects - Available projects from API
 * @returns {Object} - Validated project group
 */
export const validateProjectGroup = (projectGroup, availableProjects) => {
  const errors = [];

  // Find matching project
  const matchingProject = availableProjects.find(p =>
    p.name.toLowerCase().trim() === projectGroup.projectName.toLowerCase().trim()
  );

  if (!matchingProject) {
    errors.push(`Project "${projectGroup.projectName}" not found in system`);
    return {
      ...projectGroup,
      isValidProject: false,
      validationErrors: errors
    };
  }

  const today = moment().startOf('day');

  // Validate per-employee mobilization date
  const employeeErrors = [];
  projectGroup.employees.forEach(emp => {
    const mobDate = moment(emp.mobilization_date);
    if (!mobDate.isValid()) {
      employeeErrors.push(`${emp.employee_id}: Mobilization date is invalid`);
      return;
    }

    // Must not be after today
    if (mobDate.isAfter(today, 'day')) {
      employeeErrors.push(`${emp.employee_id}: Mobilization date should not be > current date`);
    }

    // Must be >= arrival date
    if (emp.arrival_date) {
      const arrDate = moment(emp.arrival_date);
      if (arrDate.isValid() && mobDate.isBefore(arrDate, 'day')) {
        employeeErrors.push(`${emp.employee_id}: Mobilization date must be >= arrival date`);
      }
    }
  });

  return {
    ...projectGroup,
    project_id: matchingProject.id || matchingProject.project_id,
    client_id: matchingProject.client_id,
    isValidProject: errors.length === 0 && employeeErrors.length === 0,
    validationErrors: [...errors, ...employeeErrors]
  };
};

/**
 * Format data for bulk mobilization API
 * @param {Object} projectGroup - Validated project group
 * @returns {Object} - API payload
 */
export const formatForBulkAPI = (projectGroup) => {
  return {
    mobilization_date: projectGroup.mobilization_date,
    project_id: projectGroup.project_id,
    client_id: projectGroup.client_id,
    emp_data: projectGroup.employees.map(emp => ({
      employee_id: emp.employee_id,
      employee_contract_start_Date: emp.employee_contract_start_Date,
      project_id: projectGroup.project_id,
      Basicsalary: emp.Basicsalary.toString(),
      Totalsalary: emp.Totalsalary,
      Employeelocation: emp.work_location,
      // Include all salary components
      basic: emp.basic,
      food: emp.food || 0,
      hra: emp.hra || 0,
      travel_allowance: emp.travel_allowance || 0,
      fixed_ot: emp.fixed_ot || 0,
      mobile_allowance: emp.mobile_allowance || 0,
      air_ticket_allowance: emp.air_ticket_allowance || 0,
      additional_ot: emp.additional_ot || 0,
      other_allowance: emp.other_allowance || 0
    }))
  };
};

/**
 * Download template Excel file
 * @returns {void}
 */
export const downloadTemplate = () => {
  // Create sample data
  const templateData = [
    {
      'S.NO.': 1,
      'POINT OF HIRE': 'OVERSEAS',
      'EMPLOYEE ID': 'SA48427',
      'EMPLOYEE NAME': 'MD AJON',
      'NATIONALITY': 'BANGLADESH',
      'ACTUAL PROFESSION': 'LOADING AND UNLOADING WORKER',
      'VISA PROFESSION': 'CONSTRUCTING WORKER',
      'CLIENT': 'KIRBY CONTRACTING COMPANY',
      'PROJECT': 'KIRBY BUILDING SYSTEMS',
      'VISA PROVIDER': 'ROBOU AL SOBAN COMPANY LIMITED - 7036227978356226348',
      'BORDER NUMBER': '72606416010',
      'IQAMA NUMBER': 'SHAKEEL AHMED',
      'PROJECT HEAD': 'SA15323',
      'SLA EMP ID': 'ACTIVE',
      'APPSHEET STATUS': '1-Jun-25',
      'ARRIVAL DATE': '1-Jun-25',
      'AGING (in days)': 241,
      'STATUS': 'ACTIVE',
      'CHANGE CLIENT, SALARY & OTHERS': '-',
      'SELECT': '1',
      'basic': 2500,
      'food': 200,
      'hra': 0,
      'travel_allowance': 0,
      'fixed_ot': 0,
      'mobile_allowance': 0,
      'air_ticket_allowance': 0,
      'additional_ot': 0,
      'other_allowance': 0,
      'WORK LOCATION': 'JEDDAH',
      'MOBILIZATION DATE': '2-Jun-2025'
    }
  ];
  
  // Create workbook
  const ws = XLSX.utils.json_to_sheet(templateData);
  const wb = XLSX.utils.book_new();
  
  // Add sheet with name "Sheet1"
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  
  // Download file
  XLSX.writeFile(wb, 'Mobilization_Upload_Template.xlsx');
};